import React from "react";
import { getProjects, removeProject, addProject, updateProject } from "../services/projects";
import { getClients } from "../services/clients";
import { getCelebrations } from "../services/celebrations";
import { getSectors } from "../services/sectors";
import { getTypologies } from "../services/typologies";
import { getSales } from "../services/sales";
import { getProviders } from "../services/providers";
import DataTable from "../components/Table/DataTable";
import { DeveloperBoard as DeveloperBoardIcon } from "@mui/icons-material";
import { getAgencies } from "../services/agencies";
import { roles } from "../utils/roles";
import { ClientVisual, AgencyVisual } from "../components/Table/Visual";

const headCells = [
  {
    field: "name",
    headerName: "Name",
    sortable: true,
    editable: false,
    isOnTable: true,
    type: "textField",
    flex: 1,
    minWidth: 300,
    filterable: false,
    isRequired: true,
    isOnFilter: true,
    icon: () => <DeveloperBoardIcon />,
  },
  {
    field: "previewLink",
    headerName: "Preview link",
    sortable: false,
    editable: false,
    isOnTable: false,
    cellType: "actions",
    type: "textField",
    flex: 1,
    actionType: "link",
    isRequired: true,
    helperText: "Please provide a valid link.",
  },
  {
    field: "clients",
    headerName: "Client",
    sortable: true,
    editable: false,
    isOnTable: true,
    isRequired: true,
    type: "selectAutocomplete",
    isSelectMultiple: true,
    flex: 1,
    filterable: true,
    isOnFilter: true,
    valueGetter: (value) => value.map((client) => client.name).join(", "),
    fetchAction: getClients,
    renderCell: (params) => {
      const clients = params.row.clients || [];
      if (!clients.length) return "";
      const clientName = clients[0].name;
      const clientVisual = clients[0].visual;
      return <ClientVisual name={clientName} visual={clientVisual ? `${process.env.REACT_APP_PROXY}/assets/clients/${clientVisual}` : null} />;
    },
  },
  {
    field: "sectors",
    headerName: "Sectors",
    sortable: true,
    editable: false,
    isOnTable: true,
    type: "selectAutocomplete",
    isSelectMultiple: true,
    flex: 1,
    filterable: true,
    isOnFilter: true,
    valueGetter: (value) => value.map((sector) => sector.name).join(", "),
    fetchAction: getSectors,
  },
  {
    field: "typologies",
    headerName: "Typologies",
    sortable: true,
    editable: false,
    isOnTable: true,
    type: "selectAutocomplete",
    isSelectMultiple: true,
    flex: 1,
    filterable: true,
    isOnFilter: true,
    valueGetter: (value) => value.map((typology) => typology.name).join(", "),
    fetchAction: getTypologies,
  },
  {
    field: "celebrations",
    headerName: "Celebration",
    sortable: true,
    editable: false,
    isOnTable: true,
    type: "selectAutocomplete",
    isSelectMultiple: true,
    flex: 1,
    filterable: true,
    isOnFilter: true,
    valueGetter: (value) => value.map((celebration) => celebration.name).join(", "),
    fetchAction: getCelebrations,
  },
  {
    field: "sales",
    headerName: "Sale",
    sortable: true,
    editable: false,
    isOnTable: true,
    type: "selectAutocomplete",
    isSelectMultiple: false,
    flex: 1,
    filterable: true,
    isOnFilter: true,
    valueGetter: (value) => value.map((sale) => sale.name).join(", "),
    fetchAction: getSales,
  },
  {
    field: "providers",
    headerName: "Providers",
    sortable: true,
    editable: false,
    isOnTable: true,
    type: "selectAutocomplete",
    isSelectMultiple: true,
    flex: 1,
    filterable: true,
    isOnFilter: true,
    valueGetter: (value) => value.map((provider) => provider.name).join(", "),
    fetchAction: getProviders,
  },
  {
    field: "agencies",
    headerName: "Agency",
    sortable: true,
    editable: false,
    isOnTable: true,
    isRequired: true,
    isOnEditPopin: false,
    isSuperAdminEditOnly: true,
    type: "selectAutocomplete",
    isSelectMultiple: false,
    flex: 1,
    filterable: true,
    isOnFilter: true,
    valueGetter: (value) => value.map((agency) => agency.name).join(", "),
    fetchAction: getAgencies,
    renderCell: (params) => {
      const agencies = params.row.agencies || [];
      if (!agencies.length) return "";
      const agencyName = agencies[0].name;
      const agencyVisual = agencies[0].visual;
      return <AgencyVisual name={agencyName} visual={agencyVisual ? `${process.env.REACT_APP_PROXY}/assets/agencies/${agencyVisual}` : null} />;
    },
  },
  {
    field: "date",
    headerName: "Date",
    sortable: true,
    editable: false,
    isOnTable: true,
    type: "dateTime",
    isOnFilter: true,
    isRequired: true,
    flex: 1,
    valueFormatter: (value) => new Date(value).toLocaleDateString("en-GB"),
  },
  {
    field: "canShare",
    headerName: "Shareable",
    sortable: true,
    editable: false,
    isOnTable: false,
    type: "checkbox",
    isDefaultChecked: true,
  },
  {
    field: "isTopProject",
    headerName: "Top project",
    sortable: true,
    editable: false,
    isOnTable: false,
    type: "checkbox",
    isOnFilter: true,
    isDefaultChecked: false,
    cellType: "actions",
  },
];

const entity = {
  tableId: "projects",
  label: "project",
};

const permissions = {
  read: [roles.ALL],
  delete: [roles.ALL],
  create: [roles.ALL],
  update: [roles.ALL],
};

export default function ProjectsPages() {
  return <DataTable permissions={permissions} entity={entity} fetchData={getProjects} addData={addProject} deleteData={removeProject} updateData={updateProject} headCells={headCells} sortModel={[{ field: "date", sort: "desc" }]} />;
}
